/**
 * Menu Active State Utilities
 * 
 * Utilities to determine if a menu item is active based on current pathname,
 * including logic for nested menu items (parent is active if child is active)
 */

import type { MenuItem } from "@/components/layout/DrupalMenu"

/**
 * Type for menu items (both parents and children)
 * Very flexible to accept any item with route/url
 */
type MenuItemLike = {
  route?: MenuItem['route']
  url?: string | null
  children?: any[] // Allow any children type
}

/**
 * Gets the URL from a menu item, preferring route.url over url
 */
export function getMenuItemUrl(item: MenuItemLike): string {
  // Prefer route.url if available (contains full path)
  if (item.route && typeof item.route === "object" && "url" in item.route) {
    return item.route.url as string
  }
  // Fallback to url field
  return item.url || "#"
}

/**
 * Checks if a URL is external (starts with http:// or https://)
 */
export function isExternalUrl(url: string): boolean {
  return url.startsWith("http://") || url.startsWith("https://")
}

/**
 * Normalizes a path by removing trailing slashes and ensuring leading slash
 */
export function normalizePath(path: string): string {
  // Remove trailing slash except for root "/"
  const normalized = path === "/" ? path : path.replace(/\/$/, "")
  // Ensure leading slash
  return normalized.startsWith("/") ? normalized : `/${normalized}`
}

/**
 * Checks if the current pathname matches a menu item URL
 * Supports exact match and "starts with" for parent paths
 */
export function isPathMatch(pathname: string, itemUrl: string): boolean {
  const normalizedPathname = normalizePath(pathname)
  const normalizedItemUrl = normalizePath(itemUrl)
  
  // Exact match
  if (normalizedPathname === normalizedItemUrl) {
    return true
  }
  
  // For parent paths: check if pathname starts with itemUrl
  // Example: /safer-lc/measure-123 starts with /safer-lc
  // But be careful not to match /safer-lc-other with /safer-lc
  if (normalizedPathname.startsWith(normalizedItemUrl + "/")) {
    return true
  }
  
  return false
}

/**
 * Checks if a menu item is active based on current pathname
 * 
 * A menu item is considered active if:
 * 1. The pathname matches the item's URL exactly
 * 2. The pathname starts with the item's URL (for parent paths)
 * 3. Any of the item's children URLs match the pathname
 * 
 * @param item - The menu item to check
 * @param pathname - The current pathname
 * @returns true if the menu item should be marked as active
 * 
 * @example
 * ```typescript
 * // Direct match
 * isMenuItemActive({ url: "/about" }, "/about") // true
 * 
 * // Parent path match
 * isMenuItemActive({ url: "/safer-lc" }, "/safer-lc/measure-123") // true
 * 
 * // Child match
 * isMenuItemActive({ 
 *   url: "/resources",
 *   children: [{ url: "/safer-lc" }]
 * }, "/safer-lc") // true (parent is active because child matches)
 * ```
 */
export function isMenuItemActive(
  item: MenuItemLike,
  pathname: string
): boolean {
  const itemUrl = getMenuItemUrl(item)
  
  // Skip external URLs and anchors
  if (isExternalUrl(itemUrl) || itemUrl === "#") {
    return false
  }
  
  // Check if current path matches this item's URL
  if (isPathMatch(pathname, itemUrl)) {
    return true
  }
  
  // Check if any children match (parent is active if child is active)
  if (Array.isArray(item.children) && item.children.length > 0) {
    for (const child of item.children) {
      const childUrl = getMenuItemUrl(child)
      
      // Skip external URLs and anchors
      if (isExternalUrl(childUrl) || childUrl === "#") {
        continue
      }
      
      if (isPathMatch(pathname, childUrl)) {
        return true
      }
    }
  }
  
  return false
}

/**
 * Gets all active menu items from a list of menu items
 * Useful for finding which menu items should be highlighted
 */
export function getActiveMenuItems<T extends MenuItemLike>(
  items: T[],
  pathname: string
): T[] {
  return items.filter(item => isMenuItemActive(item, pathname))
}

/**
 * Finds the first active menu item in a list
 * Useful for single-selection menus
 */
export function findActiveMenuItem<T extends MenuItemLike>(
  items: T[],
  pathname: string
): T | null {
  return items.find(item => isMenuItemActive(item, pathname)) || null
}
